import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tooltip, Typography} from "@mui/material";
import MaterialWarehouseCutOffsTable from "./MaterialWarehouseCutOffsTable";
import ButtonExit from "../../../button/ButtonExit";
import ButtonAdd from "../../../button/ButtonAdd";
import MaterialWarehouseService from "../../../../../API/orders/warehouse/material/MaterialWarehouseService";
import FormControlLabel from "@mui/material/FormControlLabel";
import {Box} from "@mui/system";
import WorkerService from "../../../../../API/orders/job/WorkerService";
import OrganizationWorkersCheckboxTable, {
    normalizeOrganizationWorkers,
} from "../../orders/organization/workerJobTable/OrganizationWorkersCheckboxTable";

const ModalFinishCutCuttingMap = ({
                                      open,
                                      handleClose,
                                      handleSubmit,
                                      currentIdRow,
                                      cuttingMapName,
                                      unableCut,
                                      setUnableCut,
                                      markedWorkers,
                                      setMarkedWorkers,
                                      handleModalAlert
                                  }) => {
    const [data, setData] = useState([]);
    const [performJob, setPerformJob] = useState(false);
    const [workers, setWorkers] = useState([]);
    const [workersLoading, setWorkersLoading] = useState(false);
    const [modalWidth, setModalWidth] = useState(null);
    const paperRef = useRef(null);

    useEffect(() => {
        if (currentIdRow !== null && currentIdRow !== undefined) {
            MaterialWarehouseService.getAllCutOffsByWorkingCuttingMapId(currentIdRow).then(function (response) {
                let data = response.data;
                data.forEach((row) => {
                    unableCut[row.id] = row.confirmedAfterCutting;
                });
                setData(data);
            })
                .catch(function (error) {
                    handleModalAlert(error);
                });
        }
    }, [currentIdRow]);

    useEffect(() => {
        if (!open) {
            setPerformJob(false);
            setModalWidth(null);
            setWorkers([]);
            setWorkersLoading(false);
            setMarkedWorkers({});
        }
    }, [open, setMarkedWorkers]);

    useEffect(() => {
        if (!performJob) {
            setWorkers([]);
            setWorkersLoading(false);
            setMarkedWorkers({});
            return undefined;
        }

        let cancelled = false;
        setWorkersLoading(true);
        WorkerService.getOrganizationWorkers()
            .then((response) => {
                if (!cancelled) {
                    setWorkers(normalizeOrganizationWorkers(response.data));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setWorkers([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setWorkersLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [performJob, setMarkedWorkers]);

    useEffect(() => {
        if (!open || performJob || !paperRef.current) {
            return undefined;
        }
        const measure = () => {
            const width = Math.round(paperRef.current.getBoundingClientRect().width);
            if (width > 0) {
                setModalWidth(width);
            }
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(paperRef.current);
        return () => observer.disconnect();
    }, [open, performJob, data.length]);

    const hasSelectedWorkers = Object.keys(markedWorkers).length > 0;
    const confirmDisabled = performJob && (!hasSelectedWorkers || workersLoading);

    const confirmButton = (
        <ButtonAdd
            text={"Подтвердить"}
            disabled={confirmDisabled}
            onClick={() => handleSubmit(performJob, markedWorkers)}
        />
    );

    const modalBody = (
        <>
            <DialogTitle textAlign="center">
                {cuttingMapName
                    ? `Подтвердить раскрой карты «${cuttingMapName}» и полученных обрезков`
                    : "Подтвердить раскрой карты и полученных обрезков"}
            </DialogTitle>
            <DialogContent>
                <form onSubmit={(e) => e.preventDefault()}>
                    <MaterialWarehouseCutOffsTable
                        data={data}
                        setData={setData}
                        unableCut={unableCut}
                        setUnableCut={setUnableCut}
                        handleModalAlert={handleModalAlert}
                    />
                    <Stack
                        sx={{
                            width: '100%',
                            minWidth: {xs: '300px', sm: '360px', md: '400px'},
                            gap: '0.01rem',
                        }}
                    >
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions sx={{p: '1.25rem', display: "flex", justifyContent: "space-between"}}>
                <FormControlLabel
                    sx={{display: "flex", flexDirection: "row-reverse", paddingLeft: "15px"}}
                    control={<Checkbox
                        onChange={() => setPerformJob(!performJob)}
                        checked={performJob}
                    />}
                    label="Отметить работу"
                />
                <Box>
                    <ButtonExit text={"Выйти"} onClick={handleClose}/>
                    {confirmDisabled ? (
                        <Tooltip title="Выберите хотя бы одного работника" arrow>
                            <Box component="span" sx={{display: 'inline-block'}}>
                                {confirmButton}
                            </Box>
                        </Tooltip>
                    ) : confirmButton}
                </Box>
            </DialogActions>
        </>
    );

    const sidePanelWidth = modalWidth ?? undefined;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={performJob ? false : undefined}
            PaperProps={{
                ref: paperRef,
                sx: performJob && sidePanelWidth ? {
                    width: sidePanelWidth * 2,
                    maxWidth: `min(${sidePanelWidth * 2}px, 96vw)`,
                } : undefined,
            }}
        >
            {performJob && sidePanelWidth ? (
                <Box sx={{display: "flex", alignItems: "stretch"}}>
                    <Box sx={{
                        flex: `0 0 ${sidePanelWidth}px`,
                        width: sidePanelWidth,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}>
                        {modalBody}
                    </Box>
                    <Box sx={{
                        flex: `0 0 ${sidePanelWidth}px`,
                        width: sidePanelWidth,
                        minWidth: 0,
                        minHeight: "100%",
                        borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}>
                        <Typography
                            component="h2"
                            sx={{
                                fontSize: "1.25rem",
                                fontWeight: 500,
                                lineHeight: 1.6,
                                textAlign: "center",
                                px: 2,
                                pt: 2,
                                pb: 1,
                                flexShrink: 0,
                            }}
                        >
                            Отметить работников
                        </Typography>
                        <Box sx={{
                            flex: "1 1 auto",
                            minHeight: 0,
                            minWidth: 0,
                            width: "100%",
                            overflowY: "auto",
                            overflowX: "auto",
                            px: 1,
                            pb: 1,
                            boxSizing: "border-box",
                        }}>
                            <OrganizationWorkersCheckboxTable
                                data={workers}
                                markedWorkers={markedWorkers}
                                setMarkedWorkers={setMarkedWorkers}
                                loading={workersLoading}
                            />
                        </Box>
                    </Box>
                </Box>
            ) : modalBody}
        </Dialog>
    );
};

export default ModalFinishCutCuttingMap;
